import React, { useState } from 'react';
import { Card, Typography, Box, Button, Chip, Grid, Skeleton, Stack, IconButton, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import { formatDistanceToNow, isAfter } from 'date-fns';
import { useQuery } from '@tanstack/react-query';

import { transactionService } from 'src/services/transaction.service';
import { Tables } from 'src/types/db.types';
import { StatCard, EmptyState } from 'src/components/roles/RoleUi';
import { chargeIcon, chargeNote, chargeTitle } from 'src/utils/chargeDisplay';
import TransactionActionModal, { ModalStep } from './TransactionActionModal';

interface PendingTransactionsPanelProps {
  studentId: string;
  currentBalance?: number;
}

type Transaction = Tables<'transactions'> & {
  transaction_type: Tables<'transaction_types'>;
  student?: {
    id: string;
    user: { firstname: string; lastname: string };
  };
};

const GREEN = '#059669';
const AMBER = '#D97706';
const RED = '#DC2626';
const BLUE = '#2563EB';

const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function dueInfo(dueDate: string | null) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  if (isAfter(new Date(), due)) return { text: `Overdue by ${formatDistanceToNow(due)}`, isOverdue: true };
  return { text: `Due ${formatDistanceToNow(due, { addSuffix: true })}`, isOverdue: false };
}

export default function PendingTransactionsPanel({
  studentId,
  currentBalance = 0,
}: PendingTransactionsPanelProps): React.JSX.Element {
  const [selected, setSelected] = useState<Transaction | null>(null);
  const [startStep, setStartStep] = useState<ModalStep>('action-selection');

  const {
    data: pendingTransactions = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['pending-transactions', studentId],
    queryFn: () => transactionService.getPendingTransactions(studentId),
    enabled: !!studentId,
    refetchInterval: 30000,
  });

  const open = (tx: Transaction, step: ModalStep) => {
    setStartStep(step);
    setSelected(tx);
  };

  if (isLoading) {
    return (
      <Grid container spacing={1.5}>
        {[0, 1, 2].map((i) => (
          <Grid item xs={12} sm={6} md={4} key={i}>
            <Skeleton variant="rounded" height={190} sx={{ borderRadius: 2.5 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const debits = pendingTransactions.filter((tx: Transaction) => tx.transaction_type.type !== 'credit');
  const totalOwed = debits.reduce((sum: number, tx: Transaction) => sum + Math.abs(Number(tx.amount || 0)), 0);
  const overdueCount = pendingTransactions.filter((tx: Transaction) => dueInfo(tx.due_date)?.isOverdue).length;
  const leftAfter = currentBalance - totalOwed;

  return (
    <Box>
      {/* Stats row, as on the Cohorts hub */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Total to Pay"
            value={money(totalOwed)}
            caption={debits.length ? 'Everything you owe right now' : 'Nothing owed'}
            icon="solar:wallet-money-bold-duotone"
            color={totalOwed > 0 ? RED : GREEN}
            valueColor={totalOwed > 0 ? RED : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Bills Waiting"
            value={pendingTransactions.length}
            caption={pendingTransactions.length ? 'Tap a card to pay' : 'All caught up'}
            icon="solar:bill-list-bold-duotone"
            color={AMBER}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Overdue"
            value={overdueCount}
            caption={overdueCount ? 'Pay these first' : 'Nothing late'}
            icon="solar:alarm-bold-duotone"
            color={overdueCount ? RED : GREEN}
            valueColor={overdueCount ? RED : undefined}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Your Balance"
            value={money(currentBalance)}
            caption={
              totalOwed === 0
                ? 'Ready to spend'
                : leftAfter >= 0
                ? `${money(leftAfter)} left after paying all`
                : `You need ${money(-leftAfter)} more to pay everything`
            }
            icon="solar:card-2-bold-duotone"
            color={BLUE}
            valueColor={leftAfter < 0 ? RED : undefined}
          />
        </Grid>
      </Grid>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
          {pendingTransactions.length ? 'Your bills' : 'No bills right now'}
        </Typography>
        <Tooltip title="Check for new bills">
          <IconButton size="small" aria-label="Check for new bills" onClick={() => refetch()}>
            <Icon icon="solar:restart-bold" width={16} />
          </IconButton>
        </Tooltip>
      </Stack>

      {pendingTransactions.length === 0 ? (
        <EmptyState
          icon="solar:shield-check-bold-duotone"
          title="All caught up!"
          message="You have no bills or charges to pay. Nice work."
        />
      ) : (
        <Grid container spacing={1.5}>
          {pendingTransactions.map((tx: Transaction) => {
            const isCredit = tx.transaction_type.type === 'credit';
            const isBill = !isCredit && tx.transaction_type.charge_mode === 'bill';
            const amount = Math.abs(Number(tx.amount || 0));
            const due = dueInfo(tx.due_date);
            const note = chargeNote(tx);
            const canAfford = isCredit || currentBalance >= amount;
            const accent = due?.isOverdue ? RED : isCredit ? GREEN : AMBER;

            return (
              <Grid item xs={12} sm={6} md={4} key={tx.id}>
                <Card
                  elevation={0}
                  sx={{
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2.5,
                    border: '1.5px solid',
                    borderColor: due?.isOverdue ? alpha(RED, 0.45) : 'divider',
                    transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                    '&:hover': { boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)', transform: 'translateY(-1px)' },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2,
                        bgcolor: alpha(accent, 0.12),
                        color: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon icon={chargeIcon(tx)} width={26} />
                    </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                      <Chip
                        label={isCredit ? 'Money in' : isBill ? 'Bill' : 'Charge'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.6875rem',
                          fontWeight: 700,
                          bgcolor: alpha(accent, 0.1),
                          color: accent,
                        }}
                      />
                      {tx.transaction_status === 'DEFERRED' && (
                        <Chip
                          label="More time"
                          size="small"
                          sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700 }}
                        />
                      )}
                      {tx.transaction_status === 'QUESTIONED' && (
                        <Chip
                          label="Question sent"
                          size="small"
                          sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                  </Stack>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    {chargeTitle(tx)}
                  </Typography>
                  {note && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                      {note}
                    </Typography>
                  )}

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      mt: 1.25,
                      color: isCredit ? GREEN : 'text.primary',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {isCredit ? '+' : ''}
                    {money(amount)}
                  </Typography>

                  <Stack spacing={0.5} sx={{ mt: 0.75, mb: 2 }}>
                    {due && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ color: due.isOverdue ? RED : 'text.secondary' }}
                      >
                        <Icon icon="solar:calendar-bold-duotone" width={15} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {due.text}
                        </Typography>
                      </Stack>
                    )}
                    {!isCredit && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                        sx={{ color: canAfford ? GREEN : AMBER }}
                      >
                        <Icon icon={canAfford ? 'solar:check-circle-bold' : 'solar:danger-triangle-bold'} width={15} />
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {canAfford
                            ? 'You have enough to pay this'
                            : `You need ${money(amount - currentBalance)} more`}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>

                  <Stack spacing={0.75} sx={{ mt: 'auto' }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => open(tx, isCredit ? 'action-selection' : 'payment-source')}
                      startIcon={<Icon icon="solar:wallet-money-bold-duotone" width={18} />}
                      sx={{ height: 40, borderRadius: 1.75, fontWeight: 700, textTransform: 'none', boxShadow: 'none' }}
                    >
                      {isCredit ? 'Review' : `Pay ${money(amount)}`}
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => open(tx, 'action-selection')}
                      sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
                    >
                      More time or a question?
                    </Button>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <TransactionActionModal
        open={!!selected}
        onClose={() => setSelected(null)}
        transaction={selected}
        studentId={studentId}
        currentBalance={currentBalance}
        initialStep={startStep}
      />
    </Box>
  );
}
